import React from 'react';

import { useGroupState, useGroupActions } from './GroupContext';
import { grantPermission, revokePermission, toggleApplyToAllUsers } from './restApi';
import Button from '../../Shared/UI/Button/components/Button';
import ButtonGroup from '../../Shared/UI/ButtonGroup/components/ButtonGroup';

import styles from './PermissionsSection.module.scss';

const APPLY_TO_ALL_USERS_OPTIONS = [
  {
    actionName: 'index_group',
    title: 'Visibility:',
    enabledLabel: 'All Users',
    disabledLabel: 'Permitted Users Only',
  },
];

const PermissionBlock = ({ permission }) => {
  const state = useGroupState();
  const { onResponse, onError } = useGroupActions();
  const { actionName, displayName, description, applyToAllUsers, roles, directUsers, eligibleMembers } = permission;

  const groupId = state?.group?.id;
  const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");

  const hasDirectUsers = directUsers && directUsers.length > 0;
  const hasEligibleMembers = eligibleMembers && eligibleMembers.length > 0;
  const allCoveredByRoles = !hasDirectUsers && !hasEligibleMembers && roles.length > 0;

  const handleGrant = (userId) => {
    grantPermission({
      id: groupId,
      onResponse,
      onError,
      body: { actionName, userId, authenticityToken },
    });
  };

  const handleRevoke = (userId) => {
    revokePermission({
      id: groupId,
      onResponse,
      onError,
      body: { actionName, userId, authenticityToken },
    });
  };

  const applyToAllOption = APPLY_TO_ALL_USERS_OPTIONS.find((opt) => opt.actionName === actionName);

  const handleToggleApplyToAll = (visible) => {
    toggleApplyToAllUsers({
      id: groupId,
      onResponse,
      onError,
      body: { actionName, visible, authenticityToken },
    });
  };

  return (
    <div className={styles.permissionBlock}>
      <div className={styles.permissionHeader}>
        <h4 className={styles.permissionName}>{displayName}</h4>
        <p className={styles.permissionDescription}>{description}</p>
      </div>

      {applyToAllOption && (
        <div className={styles.visibilityToggle}>
          <span className={styles.subsectionLabel}>{applyToAllOption.title}</span>
          <ButtonGroup>
            <Button
              variant="secondary"
              size="sm"
              pressed={!applyToAllUsers}
              onClick={() => handleToggleApplyToAll(false)}
            >
              {applyToAllOption.disabledLabel}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              pressed={applyToAllUsers}
              onClick={() => handleToggleApplyToAll(true)}
            >
              {applyToAllOption.enabledLabel}
            </Button>
          </ButtonGroup>
        </div>
      )}

      {roles.length > 0 && (
        <div className={styles.subsection}>
          <span className={styles.subsectionLabel}>Granted to roles:</span>
          <div className={styles.rolePills}>
            {roles.map((role) => (
              <span key={role.roleName} className={styles.rolePill}>
                {role.displayName}
              </span>
            ))}
          </div>
        </div>
      )}

      {allCoveredByRoles && (
        <p className={styles.coveredNote}>All members receive this permission through their roles.</p>
      )}

      {hasDirectUsers && (
        <div className={styles.subsection}>
          <span className={styles.subsectionLabel}>Direct grants:</span>
          <ul className={styles.userList}>
            {directUsers.map((user) => (
              <li key={user.id} className={styles.userRow}>
                <img
                  src={user.profilePicturePath}
                  alt={user.name}
                  className={styles.avatar}
                />
                <span className={styles.userName}>{user.name}</span>
                <Button variant="secondary" onClick={() => handleRevoke(user.id)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasEligibleMembers && (
        <div className={styles.subsection}>
          <span className={styles.subsectionLabel}>Grant to member:</span>
          <ul className={styles.userList}>
            {eligibleMembers.map((user) => (
              <li key={user.id} className={styles.userRow}>
                <img
                  src={user.profilePicturePath}
                  alt={user.name}
                  className={styles.avatar}
                />
                <span className={styles.userName}>{user.name}</span>
                <Button variant="secondary" onClick={() => handleGrant(user.id)}>
                  Grant
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PermissionBlock;
